import { getFirebaseAuth, getFirestore, getFirebaseEmailAuthProvider, deleteUser } from '../../system/firebase';
import { getAfitdAuthError } from '../authHelper';
import {
    ACCOUNT_STATUS_ACTIVE,
    ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP,
    ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION,
    AUTH_ERROR_CODE_REQUIRES_RECENT_LOGIN,
    DASHBOARD_ID_MY_SETTINGS,
    USER_TYPE_EMP,
    USER_TYPE_JS,
} from '../constants';
import {
    AFITD_SHARE_URL,
    // ANDROID_PACKAGE_NAME,
    AUTH_PROVIDER_ID_APPLE,
    AUTH_PROVIDER_ID_FACEBOOK,
    AUTH_PROVIDER_ID_GOOGLE,
    AUTH_PROVIDER_ID_TWITTER,
    // IOS_BUNDLE_ID,
    PATH_DASHBOARD,
} from '../../system/constants';
import { saveEmployerAccountLinkage } from './empAccountLinkageServices';
import { log } from '../logHelper';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _toLower from 'lodash/toLower';
import _noop from 'lodash/noop';
import _size from 'lodash/size';
import _slice from 'lodash/slice';
import _findIndex from 'lodash/findIndex';

export const signInWithEmailAndPassword = async ({ email, password }) => {
    const auth = getFirebaseAuth();
    try {
        const useCredential = await auth.signInWithEmailAndPassword(email, password);
        return useCredential;
    } catch (authError) {
        log('signInWithEmailAndPassword: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const createUserWithEmailAndPassword = async ({ email, password, firstName, lastName, isJs, isInvitationMode, invitationData, invitationCode }) => {
    const auth = getFirebaseAuth();
    try {
        const userCredential = await auth.createUserWithEmailAndPassword(email, password);

        log('Account created in Firebase!');
        const userId = _get(userCredential, 'user.uid');

        // create user/employer documents
        if (isJs) {
            await createJobSeekerUser({ userId, email, firstName, lastName });
            await createJobSeekerProfile({ userId, firstName, lastName });
        } else {
            await createEmployerUser({ userId, email, firstName, lastName, isInvitationMode, invitationData, invitationCode });
        }

        // send verification email
        await sendEmailVerification();

        return userCredential;
    } catch (authError) {
        log('createUserWithEmailAndPassword: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const createUserWithSocialMediaSignIn = async ({ userId, email, firstName, lastName, isJs, providerId }) => {
    try {
        // create user/employer documents
        if (isJs) {
            await createJobSeekerUser({ userId, email, firstName, lastName, providerId });
            await createJobSeekerProfile({ userId, firstName, lastName });
        } else {
            await createEmployerUser({ userId, email, firstName, lastName, providerId /*, isInvitationMode, invitationData, invitationCode */ });
        }
    } catch (error) {
        log('Failed to create user account', { error });
    }
};

// changing current Logged in User Password
export const changeCurrentUserPassword = async ({ newPassword, currentPassword, setSnackbarMessage, setSnackbarErrorMessage, formik }) => {
    const auth = getFirebaseAuth();
    const currentUser = auth.currentUser;

    const credentials = getFirebaseEmailAuthProvider().credential(currentUser.email, currentPassword);

    currentUser
        .reauthenticateWithCredential(credentials)
        .then(() => {
            // User re-authenticated.
            currentUser
                .updatePassword(newPassword)
                .then(() => {
                    setSnackbarMessage('Password updated successfully!');
                    formik.resetForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmNewPassword: '',
                    });
                })
                .catch(authError => {
                    const error = getAfitdAuthError(authError);
                    setSnackbarErrorMessage(error?.message);
                });
        })
        .catch(authError => {
            log({ authError }, 'Error in authenticating');
            const error = getAfitdAuthError(authError);
            setSnackbarErrorMessage(error?.message);
        });
};

export const resetPassword = async ({ newPassword, actionCode /*, setSnackbarMessage, setSnackbarErrorMessage, formik */ }) => {
    const auth = getFirebaseAuth();

    try {
        const resp = await auth.confirmPasswordReset(actionCode, newPassword);
        return resp;
    } catch (authError) {
        log('resetPassword: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

const getAuthTypeDto = providerId => {
    let authTypeDto = {};
    switch (providerId) {
        case AUTH_PROVIDER_ID_GOOGLE:
            authTypeDto = { authTypeGoog: true };
            break;
        case AUTH_PROVIDER_ID_APPLE:
            authTypeDto = { authTypeApple: true };
            break;
        case AUTH_PROVIDER_ID_FACEBOOK:
            authTypeDto = { authTypeFb: true };
            break;
        case AUTH_PROVIDER_ID_TWITTER:
            authTypeDto = { authTypeTwit: true };
            break;
        default:
            authTypeDto = { authTypeEmail: true };
    }

    return authTypeDto;
};

const getInitAccountStatus = (providerId, userType) => {
    switch (providerId) {
        case AUTH_PROVIDER_ID_GOOGLE:
        case AUTH_PROVIDER_ID_APPLE:
        case AUTH_PROVIDER_ID_FACEBOOK:
        case AUTH_PROVIDER_ID_TWITTER:
            // if signed up with social media, initialize JS status to 'A' or EMP status to 'PAS'
            return userType === USER_TYPE_JS ? ACCOUNT_STATUS_ACTIVE : userType === USER_TYPE_EMP ? ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP : ACCOUNT_STATUS_ACTIVE;
        default:
            // assume user signed up with email and password
            return ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION;
    }
};

const createJobSeekerUser = async ({ userId, email, firstName, lastName, providerId }) => {
    const db = getFirestore();
    const now = new Date();

    const authTypeDto = getAuthTypeDto(providerId);
    const initAccountStatus = getInitAccountStatus(providerId, USER_TYPE_JS);

    // Prep for users
    const userRef = db.collection('users').doc(userId);
    const userDto = {
        ...authTypeDto,
        email: _toLower(email),
        firstName,
        lastName,
        dateFormat: 'MM/dd/yyyy',
        type: USER_TYPE_JS,
        status: initAccountStatus,
        createDt: now,
    };
    try {
        // write users
        await userRef.set(userDto);
    } catch (error) {
        log('error', error);
    }
};

const createEmployerUser = async ({ userId, email, firstName, lastName, providerId, isInvitationMode, invitationData, invitationCode }) => {
    const db = getFirestore();
    const now = new Date();

    const authTypeDto = getAuthTypeDto(providerId);
    const initAccountStatus = getInitAccountStatus(providerId, USER_TYPE_EMP);

    // Prep for employers
    const employerDto = {
        inactive: false,
        dateFormat: 'MM/dd/yyyy',
        members: [
            {
                email: _toLower(email),
                firstName,
                lastName,
                isAdmin: true,
                isOwner: true,
                userId,
            },
        ],
        createDt: now,
    };
    const employersRef = db.collection('employers');

    // Prep for users (emp version)
    const userRef = db.collection('users').doc(userId);
    const userDto = {
        ...authTypeDto,
        email: _toLower(email),
        type: USER_TYPE_EMP,
        // status: ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP,
        createDt: now,
    };

    // Prep for openInvitations
    const openInvitationRef = db.collection('openInvitations').doc(email);
    let openInviteCount = 0;

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            // read openInvitations
            const openInvitationDoc = await transaction.get(openInvitationRef);
            if (openInvitationDoc.exists) {
                const openInvitationData = openInvitationDoc.data();
                openInviteCount = _get(openInvitationData, 'count', 0);
            }

            // write employers
            const employerRef = await employersRef.add(employerDto);

            // include employerId, openInviteCount in userDto
            _set(userDto, 'employers', [{ employerId: employerRef.id, firstName, lastName, status: initAccountStatus }]);
            _set(userDto, 'openInviteCount', openInviteCount);

            // Include a special flag if user is creating an account with an invitation
            if (isInvitationMode || openInviteCount > 0) {
                _set(userDto, 'isInviteMode', true);
            }

            // write users
            await transaction.set(userRef, userDto);
        });

        // If invitation mode and the invitationData is provided, go ahead and link the employer
        if (isInvitationMode && invitationData) {
            // set up the params for saveEmployerAccountLinkage()
            const inviteeUserData = {
                employers: [
                    {
                        firstName,
                        lastName,
                    },
                ],
            };
            const invitationList = [{ ...invitationData, code: invitationCode, isAccept: true }];

            // link the employer account
            await saveEmployerAccountLinkage({ inviteeUserId: userId, inviteeUserData, invitationList, setSnackbarMessage: _noop, setSnackbarErrorMessage: _noop });
        }
    } catch (error) {
        log('error', error);
    }
};

const createJobSeekerProfile = async ({ userId, firstName, lastName }) => {
    const db = getFirestore();
    // const now = new Date();

    // Prep for users
    const profileRef = db.collection('profiles').doc(userId);
    const dto = {
        firstName,
        lastName,
    };
    try {
        // write profile
        await profileRef.set(dto);
    } catch (error) {
        log('error', error);
    }
};

export const sendEmailVerification = async () => {
    const auth = getFirebaseAuth();
    const authUser = _get(auth, 'currentUser');
    try {
        if (authUser) {
            // send verification email
            await authUser.sendEmailVerification({
                url: `https://${AFITD_SHARE_URL}/${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}`,
                // iOS: { bundleId: IOS_BUNDLE_ID },
                // android: { installApp: true, packageName: ANDROID_PACKAGE_NAME },
            });

            return true;
        } else {
            throw new Error('Auth user is undefined');
        }
    } catch (authError) {
        log('sendEmailVerification: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const applyActionCode = async ({ code }) => {
    const auth = getFirebaseAuth();
    try {
        await auth.applyActionCode(code);
    } catch (authError) {
        log('applyActionCode: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const verifyPasswordResetCode = async ({ code }) => {
    const auth = getFirebaseAuth();
    try {
        await auth.verifyPasswordResetCode(code);
    } catch (authError) {
        log('verifyPasswordResetCode: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const checkUserExists = async ({ email }) => {
    const auth = getFirebaseAuth();
    try {
        if (email) {
            const signInMethods = await auth.fetchSignInMethodsForEmail(email);
            return signInMethods.length > 0;
        } else {
            throw new Error('Provided email address is null or undefined');
        }
    } catch (authError) {
        log('checkUserExists: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const activateUserAccountAfterVerifiedEmail = async ({ userId }) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const userRef = db.collection('users').doc(userId);

    try {
        await db.runTransaction(async transaction => {
            // This code may get re-run multiple times if there are conflicts.
            //-------------------------------------------------------------------------------------------
            // read users
            //-------------------------------------------------------------------------------------------
            const userDoc = await transaction.get(userRef);
            if (userDoc.exists) {
                const userData = userDoc.data();
                const { type, employers } = userData;
                let userStatusDto = {};

                const status = type === USER_TYPE_JS ? _get(userData, 'status') : _get(employers, '[0].status');

                if (status === ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION) {
                    // check user type
                    if (type === USER_TYPE_JS) {
                        // job seeker user
                        userStatusDto = { status: ACCOUNT_STATUS_ACTIVE };
                    } else {
                        // employer user
                        if (_size(employers) > 0) {
                            const firstEmployerItem = { ...employers[0], status: ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP };
                            const updatedEmployers = [firstEmployerItem, ..._slice(employers, 1)];
                            userStatusDto = { employers: updatedEmployers };
                        } else {
                            throw new Error("User's 'employers' data not exist");
                        }
                    }

                    //-------------------------------------------------------------------------------------------
                    // write users
                    //-------------------------------------------------------------------------------------------
                    transaction.set(userRef, userStatusDto, { merge: true });
                }
            }
        });
    } catch (error) {
        console.error('Failed to activate user account', error);
    }
};

export const activeEmpMemberAfterSetUp = async ({ userId, employerId }) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const userRef = db.collection('users').doc(userId);

    try {
        await db.runTransaction(async transaction => {
            // This code may get re-run multiple times if there are conflicts.
            //-------------------------------------------------------------------------------------------
            // read users
            //-------------------------------------------------------------------------------------------
            const userDoc = await transaction.get(userRef);
            if (userDoc.exists) {
                const userData = userDoc.data();
                const { employers = [] } = userData;
                const idx = _findIndex(employers, item => item.employerId === employerId);

                if (idx >= 0) {
                    // found member employer item in users doc
                    const memberAccountStatus = employers[idx].status;

                    // activate account only if it is not already active
                    if (memberAccountStatus !== ACCOUNT_STATUS_ACTIVE) {
                        _set(employers, `[${idx}].status`, ACCOUNT_STATUS_ACTIVE);

                        //-------------------------------------------------------------------------------------------
                        // write users
                        //-------------------------------------------------------------------------------------------
                        await transaction.set(userRef, { employers }, { merge: true });
                    }

                    return true;
                } else {
                    throw new Error('Employer member record not found.');
                }
            }
        });
    } catch (error) {
        console.error('Failed to activate user account', error);
        return false;
    }
};

export const sendResetPasswordEmail = async ({ email }) => {
    const auth = getFirebaseAuth();
    try {
        const resp = await auth.sendPasswordResetEmail(email);
        return resp;
    } catch (authError) {
        log('sendResetPasswordEmail: authError ', authError);
        const error = getAfitdAuthError(authError);
        throw error;
    }
};

export const deleteUserAccount = async () => {
    try {
        await deleteUser();
    } catch (error) {
        console.log('error', error);
        if (error.message === AUTH_ERROR_CODE_REQUIRES_RECENT_LOGIN) throw new Error(AUTH_ERROR_CODE_REQUIRES_RECENT_LOGIN);
        else return null;
    }
};
